import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import alertService from "../../../../_services/cart/alert.service";
import facturacionService, {
    estadoFacturaLabel, fechaFactura, importeFactura, letra, mensajeDeError, numeroFormateado,
} from "../../../../_services/cart/facturacion.service";

/**
 * Solapa "Factura" del detalle de una venta, del lado del vendedor. Solo se monta con ARCA integrado y
 * probado (lo decide CompraDetail con `facturacionService.habilitada()`).
 *
 * Por defecto sale a consumidor final con el nombre y el mail del comprador, que es lo que corresponde a
 * casi toda venta de autopartes a un particular. Si el comprador pide factura a su nombre (un taller con
 * CUIT, por ejemplo), se cargan sus datos. El tipo de comprobante (A/B/C) no se elige: lo decide el back
 * con las dos condiciones frente al IVA, igual que tienda-admin.
 */

const CONDICIONES_RECEPTOR = [
    { value: "RESPONSABLE_INSCRIPTO", label: "IVA Responsable Inscripto" },
    { value: "MONOTRIBUTISTA", label: "Responsable Monotributo" },
    { value: "EXENTO", label: "IVA Sujeto Exento" },
    { value: "CONSUMIDOR_FINAL", label: "Consumidor Final" },
    { value: "NO_CATEGORIZADO", label: "Sujeto No Categorizado" },
];

/** Las mismas que el back acepta para facturar: el pago tiene que estar. */
const ESTADOS_FACTURABLES = ["PAGADA", "PENDIENTE_ENTREGA", "FINALIZADA"];

export const FacturaVenta = ({ venta }) => {
    const nombreComprador = `${venta.comprador?.firstName || ""} ${venta.comprador?.lastName || ""}`.trim();

    const [facturas, setFacturas] = useState(null);
    const [abierto, setAbierto] = useState(false);
    const [emitiendo, setEmitiendo] = useState(false);
    const [trabajando, setTrabajando] = useState(null);
    const [form, setForm] = useState({
        receptor: "CONSUMIDOR_FINAL", // CONSUMIDOR_FINAL | DNI | CUIT
        docNro: "",
        razonSocial: nombreComprador,
        condicionIva: "CONSUMIDOR_FINAL",
        domicilio: "",
        email: venta.comprador?.email || "",
    });

    const cargar = () => facturacionService.deVenta(venta.id)
        .then(setFacturas)
        .catch(() => setFacturas([]));

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { cargar(); }, [venta.id]);

    const set = (campo) => (e) => {
        const cambio = { [campo]: e.target.value };
        // Quien pide factura con CUIT casi siempre es un comercio inscripto: es la que le da la A.
        if (campo === "receptor") {
            cambio.condicionIva = e.target.value === "CUIT" ? "RESPONSABLE_INSCRIPTO" : "CONSUMIDOR_FINAL";
        }
        setForm({ ...form, ...cambio });
    };

    const viva = (facturas || []).find((f) => f.estado === "EMITIDA" || f.estado === "BORRADOR");
    const rechazadas = (facturas || []).filter((f) => f.estado === "RECHAZADA");
    const facturable = ESTADOS_FACTURABLES.includes(venta.estado);

    const emitir = () => {
        const datos = {
            receptorEmail: form.email.trim() || undefined,
            receptorRazonSocial: form.razonSocial.trim() || undefined,
            receptorDomicilio: form.domicilio.trim() || undefined,
        };
        if (form.receptor === "CONSUMIDOR_FINAL") {
            datos.receptorCondicionIva = "CONSUMIDOR_FINAL";
        } else {
            const nro = form.docNro.replace(/\D/g, "");
            if (form.receptor === "CUIT" && nro.length !== 11) {
                alertService.msg("El CUIT tiene que tener 11 dígitos", "error");
                return;
            }
            if (form.receptor === "DNI" && (nro.length < 7 || nro.length > 8)) {
                alertService.msg("Revisá el número de DNI", "error");
                return;
            }
            if (!form.razonSocial.trim()) {
                alertService.msg("Completá el nombre o la razón social", "error");
                return;
            }
            datos.receptorDocTipo = form.receptor;
            datos.receptorDocNro = nro;
            datos.receptorCondicionIva = form.receptor === "CUIT" ? form.condicionIva : "CONSUMIDOR_FINAL";
        }
        setEmitiendo(true);
        facturacionService.facturarVenta(venta.id, datos)
            .then((f) => {
                if (f.estado === "EMITIDA") {
                    alertService.msg(`Factura ${letra(f)} ${numeroFormateado(f)} emitida`, "success");
                    setAbierto(false);
                } else if (f.estado === "BORRADOR") {
                    alertService.msg("No se pudo hablar con ARCA. La factura quedó para reintentar.", "error");
                } else {
                    alertService.msg(f.erroresAfip || "ARCA rechazó la factura", "error");
                }
                cargar();
            })
            .catch((err) => alertService.msg(mensajeDeError(err, "No se pudo emitir la factura"), "error"))
            .finally(() => setEmitiendo(false));
    };

    const pdf = (f) => {
        setTrabajando(f.id);
        facturacionService.descargarPdf(f)
            .catch((err) => alertService.msg(mensajeDeError(err, "No se pudo descargar el PDF"), "error"))
            .finally(() => setTrabajando(null));
    };

    const reintentar = (f) => {
        setTrabajando(f.id);
        facturacionService.reintentar(f.id)
            .then((r) => {
                if (r.estado === "EMITIDA") alertService.msg(`Factura ${letra(r)} ${numeroFormateado(r)} emitida`, "success");
                else alertService.msg(r.erroresAfip || "ARCA no aprobó la factura", "error");
                cargar();
            })
            .catch((err) => alertService.msg(mensajeDeError(err, "No se pudo reintentar"), "error"))
            .finally(() => setTrabajando(null));
    };

    if (facturas === null) return <p className="panel__estado-carga">Cargando…</p>;

    return (
        <div className="factura-venta">
            {viva && (
                <div className="factura-venta__emitida">
                    <div>
                        <p className="cuenta-usuarios__eyebrow">{viva.estado === "EMITIDA" ? "FACTURA EMITIDA" : "FACTURA SIN ENVIAR A ARCA"}</p>
                        <p className="factura-venta__numero">
                            {letra(viva) ? `Factura ${letra(viva)}` : viva.tipoComprobante} {numeroFormateado(viva)}
                        </p>
                        <p className="arca__nota">
                            {fechaFactura(viva.fechaEmision)} · {viva.receptorRazonSocial} · {importeFactura(viva.importeTotal)}
                            {viva.cae && <> · CAE {viva.cae}</>}
                        </p>
                        {viva.estado === "EMITIDA" && viva.mailEstado === "ENVIADO" && (
                            <p className="arca__nota">Se le mandó por mail a {viva.receptorEmail}.</p>
                        )}
                        {viva.estado === "BORRADOR" && viva.erroresAfip && <p className="arca__nota facturas__error">{viva.erroresAfip}</p>}
                    </div>
                    <div className="arca__fila">
                        {viva.estado === "EMITIDA" && (
                            <button type="button" className="button" data-type="secondary" onClick={() => pdf(viva)} disabled={trabajando === viva.id}>
                                <FontAwesomeIcon icon="fa-solid fa-file-pdf" /> Descargar PDF
                            </button>
                        )}
                        {viva.estado === "BORRADOR" && (
                            <button type="button" className="button" data-type="accent" onClick={() => reintentar(viva)} disabled={trabajando === viva.id}>
                                {trabajando === viva.id ? "Enviando…" : "Reintentar"}
                            </button>
                        )}
                    </div>
                </div>
            )}

            {!viva && !facturable && (
                <p className="arca__nota">La venta se puede facturar cuando esté pagada.</p>
            )}

            {!viva && facturable && !abierto && (
                <div className="arca__fila">
                    <p className="arca__nota">Esta venta todavía no tiene factura.</p>
                    <button type="button" className="button" data-type="accent" onClick={() => setAbierto(true)}>
                        <FontAwesomeIcon icon="fa-solid fa-file-invoice-dollar" /> Facturar
                    </button>
                </div>
            )}

            {!viva && facturable && abierto && (
                <div className="factura-venta__form">
                    <div className="factura-venta__opciones">
                        {[["CONSUMIDOR_FINAL", "Consumidor final"], ["DNI", "Con DNI"], ["CUIT", "Con CUIT"]].map(([v, l]) => (
                            <label key={v} className={form.receptor === v ? "factura-venta__opcion factura-venta__opcion--activa" : "factura-venta__opcion"}>
                                <input type="radio" name="receptor" value={v} checked={form.receptor === v} onChange={set("receptor")} />
                                {l}
                            </label>
                        ))}
                    </div>

                    <div className="arca__grid">
                        {form.receptor !== "CONSUMIDOR_FINAL" && (
                            <label className="arca__campo">
                                <span className="cuenta-usuarios__label">{form.receptor === "CUIT" ? "CUIT (sin guiones)" : "DNI"}</span>
                                <input type="text" value={form.docNro} onChange={set("docNro")} maxLength={form.receptor === "CUIT" ? 11 : 8} />
                            </label>
                        )}
                        {form.receptor === "CUIT" && (
                            <label className="arca__campo">
                                <span className="cuenta-usuarios__label">Condición frente al IVA</span>
                                <select className="arca__select" value={form.condicionIva} onChange={set("condicionIva")}>
                                    {CONDICIONES_RECEPTOR.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                                </select>
                            </label>
                        )}
                        <label className="arca__campo arca__campo--ancho">
                            <span className="cuenta-usuarios__label">{form.receptor === "CUIT" ? "Razón social" : "Nombre"}</span>
                            <input type="text" value={form.razonSocial} onChange={set("razonSocial")} maxLength={200} />
                        </label>
                        {form.receptor !== "CONSUMIDOR_FINAL" && (
                            <label className="arca__campo arca__campo--ancho">
                                <span className="cuenta-usuarios__label">Domicilio</span>
                                <input type="text" value={form.domicilio} onChange={set("domicilio")} maxLength={300} />
                            </label>
                        )}
                        <label className="arca__campo arca__campo--ancho">
                            <span className="cuenta-usuarios__label">Mandar la factura a</span>
                            <input type="email" value={form.email} onChange={set("email")} />
                        </label>
                    </div>

                    <p className="arca__nota">
                        Total {importeFactura(venta.total)}. La factura es <strong>legalmente válida e irreversible</strong>: revisá los datos antes de emitirla.
                    </p>
                    <div className="arca__acciones">
                        <button type="button" className="button" data-type="secondary" onClick={() => setAbierto(false)} disabled={emitiendo}>
                            Cancelar
                        </button>
                        <button type="button" className="button" data-type="accent" onClick={emitir} disabled={emitiendo}>
                            {emitiendo ? "Emitiendo con ARCA…" : "Emitir factura"}
                        </button>
                    </div>
                </div>
            )}

            {rechazadas.length > 0 && (
                <div className="factura-venta__rechazadas">
                    <p className="cuenta-usuarios__eyebrow">INTENTOS RECHAZADOS POR ARCA</p>
                    {rechazadas.map((f) => (
                        <p key={f.id} className="arca__nota">
                            {fechaFactura(f.fechaEmision)} · {estadoFacturaLabel(f.estado)}: <span className="facturas__error">{f.erroresAfip || "sin detalle"}</span>
                        </p>
                    ))}
                </div>
            )}
        </div>
    );
};
